import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "../components/common/Modal";
import CourseForm from "../components/courses/CourseForm";
import api from "../services/api";
import Swal from "sweetalert2";
import courseService from "../services/courseService";

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coursesResponse, usersResponse] = await Promise.all([
        api.get("/courses"),
        api.get("/users"),
      ]);
      setCourses(coursesResponse.data.data);
      const instructorList = usersResponse.data.data
        .filter((inst) => inst.role && inst.role.name === "instructor")
        .map((inst) => ({
          _id: inst._id,
          name: `${inst.firstName} ${inst.lastName}`,
        }));
      setInstructors(instructorList);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load courses data");
    } finally {
      setLoading(false);
    }
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const durationInMs = end - start;
    const durationInDays = Math.ceil(durationInMs / (1000 * 60 * 60 * 24));
    return `${durationInDays} days`;
  };

  const handleAddCourse = async (courseData) => {
    try {
      await api.post("/courses", courseData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Course added successfully");
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      console.error("Error adding course:", error);
      toast.error(error.response?.data?.message || "Failed to add course");
    }
  };

  const handleEditCourse = async (courseData) => {
    try {
      const token = localStorage.getItem("authToken");
      await courseService.updateCourse(selectedCourse._id, courseData, token);
      toast.success("Course updated successfully");
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      console.error("Error updating course:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
        toast.error(error.response.data.message || "Failed to update course");
      } else {
        toast.error("Failed to update course");
      }
    }
  };

  const handleDeleteCourse = async (courseId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/courses/${courseId}`);
        toast.success("Course deleted successfully");
        fetchData();
      } catch (error) {
        console.error("Error deleting course:", error);
        toast.error(error.response?.data?.message || "Failed to delete course");
      }
    }
  };

  return (
    <div className="p-8 space-y-8 bg-white rounded-lg shadow-xl courses-container">
      <div className="flex items-center justify-between mb-8 page-header">
        <h1 className="text-3xl font-semibold text-gray-900">Courses</h1>
        <button
          className="px-8 py-3 text-white transition-all bg-indigo-600 rounded-lg shadow-md add-button hover:bg-green-700"
          onClick={() => setShowAddModal(true)}
        >
          Add Course
        </button>
      </div>
      {loading ? (
        <div className="py-6 text-xl text-center text-gray-500 loading-spinner">
          <div className="w-12 h-12 mx-auto border-b-4 border-indigo-600 rounded-full animate-spin"></div>
          <p className="mt-4 text-lg text-gray-600">Loading courses...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {courses.map((course) => (
            <div
              key={course._id}
              className="overflow-hidden transition-shadow duration-300 bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-lg"
            >
              {course.imageUrl && (
                <div className="h-48 overflow-hidden">
                  <img
                    src={
                      course.imageUrl.startsWith("http")
                        ? course.imageUrl
                        : `http://localhost:5000${course.imageUrl}`
                    }
                    alt={course.name}
                    className="object-cover w-full h-full"
                  />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <h3 className="mb-2 text-xl font-semibold text-gray-800">
                    {course.name}
                  </h3>
                  <span
                    className={`status-badge text-white rounded-md py-1 px-3 text-xs ${
                      course.status === "active"
                        ? "bg-green-500"
                        : course.status === "upcoming"
                        ? "bg-blue-500"
                        : "bg-gray-500"
                    }`}
                  >
                    {course.status.charAt(0).toUpperCase() +
                      course.status.slice(1)}
                  </span>
                </div>
                <p className="mb-1 text-gray-600">
                  <span className="font-medium">Instructor:</span>{" "}
                  {course.instructor
                    ? `${course.instructor.firstName} ${course.instructor.lastName}`
                    : "Not Assigned"}
                </p>
                <p className="mb-1 text-gray-600">
                  <span className="font-medium">Duration:</span>{" "}
                  {calculateDuration(course.startDate, course.endDate)}
                </p>
                <p className="mb-1 text-gray-600">
                  <span className="font-medium">Starts:</span>{" "}
                  {new Date(course.startDate).toLocaleDateString()}
                </p>
                <p className="mb-4 text-gray-600">
                  <span className="font-medium">Price:</span> $
                  {course.price !== undefined && course.price !== null
                    ? course.price
                    : 0}
                </p>
                <div className="flex space-x-2">
                  <button
                    className="px-3 py-1 text-sm text-white transition-all bg-blue-600 rounded-md view-button hover:bg-blue-700"
                    onClick={() => {
                      setSelectedCourse(course);
                      setShowViewModal(true);
                    }}
                  >
                    View
                  </button>
                  <button
                    className="px-3 py-1 text-sm text-white transition-all bg-yellow-500 rounded-md edit-button hover:bg-yellow-600"
                    onClick={() => {
                      setSelectedCourse(course);
                      setShowEditModal(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="px-3 py-1 text-sm text-white transition-all bg-red-600 rounded-md delete-button hover:bg-red-700"
                    onClick={() => handleDeleteCourse(course._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showAddModal && (
        <Modal
          title="Add Course"
          onClose={() => setShowAddModal(false)}
          size="large"
        >
          <CourseForm
            instructors={instructors}
            onSave={handleAddCourse}
            onCancel={() => setShowAddModal(false)}
          />
        </Modal>
      )}
      {showEditModal && selectedCourse && (
        <Modal
          title="Edit Course"
          onClose={() => setShowEditModal(false)}
          size="large"
        >
          <CourseForm
            course={{
              ...selectedCourse,
              instructor: selectedCourse.instructor?._id || "",
              startDate: selectedCourse.startDate
                ? new Date(selectedCourse.startDate).toISOString().split("T")[0]
                : "",
              endDate: selectedCourse.endDate
                ? new Date(selectedCourse.endDate).toISOString().split("T")[0]
                : "",
              price: selectedCourse.price,
              status: selectedCourse.status,
            }}
            instructors={instructors}
            onSave={handleEditCourse}
            onCancel={() => setShowEditModal(false)}
          />
        </Modal>
      )}
      {showViewModal && selectedCourse && (
        <Modal
          title="View Course"
          onClose={() => setShowViewModal(false)}
          size="large"
        >
          <CourseForm
            course={{
              ...selectedCourse,
              instructor: selectedCourse.instructor?._id || "",
              startDate: selectedCourse.startDate
                ? new Date(selectedCourse.startDate).toISOString().split("T")[0]
                : "",
              endDate: selectedCourse.endDate
                ? new Date(selectedCourse.endDate).toISOString().split("T")[0]
                : "",
              price: selectedCourse.price,
              status: selectedCourse.status,
            }}
            instructors={instructors}
            readOnly={true}
            onCancel={() => setShowViewModal(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default Courses;
